import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Grid, Link, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/authContext';
import { useTheme } from '../../context/ThemeContext';

import api from '../../services/api';
import {
  AnimationContainer,
  Container,
  Content,
  FormBox,
  BackgroundImg,
  LoginTextField,

  // BackgroundDiv,
} from './styles';
import { validateEmail } from '../../utils/emailValidation';

import logoAnalyticImg from '../../assets/logo-insights-white.png';

interface SignInFormData {
  email: string;
  password: string;
}

const SignIn: React.FC = () => {
  const { signIn } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const loginPageTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
    },
  });
  const [showPwd, setShowPwd] = useState<boolean>(false);
  const [messageFirstPwd, setMessageFirstPwd] = useState<string>('');
  const [messageWeakPwd, setMessageWeakPwd] = useState<string>('');
  const [errorPassword, setErrorPassword] = useState<boolean>(false);
  const [errorEmail, setErrorEmail] = useState<boolean>(false);
  const [helperTextPassword, setHelperTextPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = (): void => {
    setShowPassword(prev => !prev);
  };

  const checkPassword = useCallback(async (email: string) => {
    try {
      const response = await api.post('/users/checkPassword', {
        email,
        isReset: false,
      });
      return response.data;
    } catch (error: any) {
      console.error(error?.response?.data?.message ?? error?.message ?? error);
      return {};
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const user: SignInFormData = {
        email: data?.get('email')?.toString().toLowerCase() ?? '',
        password: data?.get('password')?.toString() ?? '',
      };

      const isValidEmail = validateEmail(user.email);

      if (isValidEmail) {
        setErrorEmail(false);
        if (showPwd) {
          const logged = await signIn(user);
          if (!logged.weakPassword) {
            if (logged.signIn) {
              navigate(logged.isGuest ? '/tasks' : '/');
            } else {
              setErrorPassword(true);
              setHelperTextPassword('Falsche E-Mail/Passwort-Kombination');
            }
          } else {
            setMessageWeakPwd(
              'Wir führen eine Sicherheitsverstärkung bei Insights durch und bitten unsere Kunden, ihre Passwörter zurückzusetzen. Sie haben eine E-Mail mit einem Link erhalten, über den Sie Ihr Passwort zurücksetzen können. Wir danken Ihnen für Ihr Verständnis.',
            );
          }
        } else {
          const hasPassword = await checkPassword(user.email);
          if (hasPassword.status) {
            setShowPwd(true);
          } else {
            setMessageFirstPwd(hasPassword.message);
          }
        }
      } else {
        setErrorEmail(true);
      }
    },
    [checkPassword, navigate, showPwd, signIn],
  );

  return (
    <Container>
      <ThemeProvider theme={loginPageTheme}>
        <Content>
          <AnimationContainer>
            <img
              src={logoAnalyticImg}
              alt="Insights"
              style={{ maxWidth: '61%', marginTop: '-113px' }}
            />
            <FormBox as="form" onSubmit={handleSubmit} noValidate>
              <LoginTextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-Mail Adresse"
                name="email"
                error={errorEmail}
                helperText={errorEmail ? 'Invalid email format' : ''}
                autoComplete="email"
                autoFocus
              />

              {showPwd && (
                <LoginTextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Passwort"
                  error={errorPassword}
                  helperText={helperTextPassword}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <Button
                type="submit"
                fullWidth
                style={{
                  backgroundColor: '#BCD0DA',
                  color: '#47283',
                  height: '55px',
                }}
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                // onClick={handleSubmit}
              >
                {showPwd ? 'Anmelden' : 'Weiter'}
              </Button>
            </FormBox>
            {messageFirstPwd !== '' && (
              <h4 style={{ color: '#4D50FF', margin: '0px 4dvw 3dvh 8dvw' }}>
                {messageFirstPwd}
              </h4>
            )}
            {messageWeakPwd !== '' && (
              <h4 style={{ color: '#ffc100', fontSize: 'large' }}>
                {messageWeakPwd}
              </h4>
            )}
            {messageWeakPwd === '' && (
              <Grid container style={{ color: '#bcd0da', width: '55%' }}>
                <Grid item xs>
                  <Link href="/forgotPassword" variant="body2">
                    Passwort vergessen?
                  </Link>
                </Grid>
              </Grid>
            )}
          </AnimationContainer>
        </Content>
        <BackgroundImg />
      </ThemeProvider>
    </Container>
  );
};

export default SignIn;
